import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import SocialLoginButtons from "@/components/SocialLoginButtons";
import DevAccountSwitcher from "@/components/DevAccountSwitcher";
import AuthShell from "@/components/AuthShell";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClientError } from "graphql-request";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import type { User } from "@/lib/auth";
import { oauthErrorKey, readOAuthRefusal } from "@/lib/oauthErrors";

// Validate a ?returnTo= against a strict first-party allowlist before honouring
// it as a post-login redirect (#216) — an unchecked returnTo is an open-redirect.
// Only the marketplace host and its `admin.` subdomain are allowed (covers the
// admin → public sign-in → back-to-admin round-trip; the session cookie is scoped
// to the parent domain so it carries across). Anything else → null (fall back to
// the normal dashboard redirect).
export function safeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const base = window.location.host.replace(/^admin\./, "");
  const allowed = new Set([base, `admin.${base}`]);
  if (!allowed.has(url.host)) return null;
  return url.toString();
}

const SignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Two-step (2FA) state. When the password is accepted but 2FA is on, the
  // server hands back a challenge token and we switch to the code step.
  const [step, setStep] = useState<"credentials" | "twoFactor">("credentials");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");

  // Surface an OAuth refusal passed back as ?error_code= by OmniauthSessionsController
  // (a pre-codes ?error=<sentence> link is still shown as is, for one release).
  useEffect(() => {
    const refusal = readOAuthRefusal(window.location.search);
    if (refusal) {
      toast({
        title: t("auth.signIn.toast.signInFailedTitle"),
        description: refusal.legacyMessage ?? t(oauthErrorKey(refusal.code), { provider: refusal.provider }),
        variant: "destructive",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const goToDashboard = (user: User) => {
    // If we arrived from a protected page (e.g. the admin subdomain) with a valid
    // first-party returnTo, send the user straight back there instead of the
    // default dashboard (#216). window.location (not navigate) because returnTo
    // may be a different subdomain the SPA router can't reach.
    const returnTo = safeReturnTo(new URLSearchParams(window.location.search).get("returnTo"));
    if (returnTo) {
      window.location.href = returnTo;
      return;
    }
    navigate(user.role === "tutor" ? "/tutor/classroom" : "/student/classroom");
  };

  // graphql-request's ClientError.message (and the error object itself) embeds
  // the request variables — the typed password / 2FA code — so neither may be
  // rendered or logged. Surface only the response-side message.
  const describeError = (err: unknown) =>
    err instanceof ClientError
      ? (err.response.errors?.[0]?.message ?? t("auth.signIn.toast.genericError"))
      : err instanceof Error
        ? err.message
        : t("auth.signIn.toast.genericError");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { user, errors, twoFactorRequired, challengeToken: token } = await signIn(email, password);
      if (user) {
        goToDashboard(user);
      } else if (twoFactorRequired && token) {
        setChallengeToken(token);
        setOtpCode("");
        setStep("twoFactor");
      } else {
        toast({ title: t("auth.signIn.toast.signInFailedTitle"), description: errors[0], variant: "destructive" });
      }
    } catch (err) {
      toast({ title: t("auth.signIn.toast.errorTitle"), description: describeError(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeToken) return;
    setLoading(true);
    try {
      const code = useBackup ? backupCode.trim() : otpCode.trim();
      const { user, errors } = await verifyTwoFactor(challengeToken, code);
      if (user) {
        goToDashboard(user);
      } else {
        toast({ title: t("auth.signIn.toast.verificationFailedTitle"), description: errors[0], variant: "destructive" });
        setOtpCode("");
        setBackupCode("");
        // An expired/used challenge means starting over from the password step.
        if (errors[0]?.toLowerCase().includes("sign in again")) {
          setStep("credentials");
          setChallengeToken(null);
        }
      }
    } catch (err) {
      toast({ title: t("auth.signIn.toast.errorTitle"), description: describeError(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell belowCard={import.meta.env.DEV && step === "credentials" && <DevAccountSwitcher />}>
        <Card className="border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{step === "twoFactor" ? t("auth.signIn.twoFactor.title") : t("auth.signIn.title")}</CardTitle>
            <CardDescription>
              {step === "twoFactor"
                ? t("auth.signIn.twoFactor.description")
                : t("auth.signIn.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === "twoFactor" ? (
              <form onSubmit={handleVerify} className="space-y-5">
                {useBackup ? (
                  <div className="space-y-2">
                    <Label htmlFor="backup-code">{t("auth.signIn.twoFactor.backupCodeLabel")}</Label>
                    <Input
                      id="backup-code"
                      autoFocus
                      autoComplete="one-time-code"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value)}
                      placeholder={t("auth.signIn.twoFactor.backupCodePlaceholder")}
                      className="text-center tracking-widest"
                    />
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} autoFocus>
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || (useBackup ? backupCode.trim().length === 0 : otpCode.length < 6)}
                >
                  {loading ? t("auth.signIn.twoFactor.verifying") : t("auth.signIn.twoFactor.verify")}
                </Button>

                <div className="space-y-2 text-center">
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:underline rounded-sm focus-ring"
                    onClick={() => {
                      setUseBackup((v) => !v);
                      setOtpCode("");
                      setBackupCode("");
                    }}
                  >
                    {useBackup
                      ? t("auth.signIn.twoFactor.useAuthenticatorCode")
                      : t("auth.signIn.twoFactor.useBackupCode")}
                  </button>
                  <div>
                    <button
                      type="button"
                      className="text-xs text-foreground hover:underline rounded-sm focus-ring"
                      onClick={() => { setStep("credentials"); setChallengeToken(null); setOtpCode(""); setBackupCode(""); setUseBackup(false); }}
                    >
                      {t("auth.signIn.twoFactor.backToSignIn")}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <SocialLoginButtons />

              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.signIn.emailLabel")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.signIn.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.signIn.passwordLabel")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:underline">
                    {t("auth.signIn.forgotPassword")}
                  </Link>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t("auth.signIn.submitting") : t("auth.signIn.submit")}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("auth.signIn.noAccountPrompt")}{" "}
              <Link to="/signup" className="text-foreground hover:underline font-medium">{t("auth.signIn.signUpLink")}</Link>
            </p>
              </>
            )}
          </CardContent>
        </Card>
    </AuthShell>
  );
};

export default SignIn;
